import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button, Modal } from "react-bootstrap"
import { getOrganisations, getOrganisationDetail } from "../../../requesters/Backend/OrganisationRequester"
import { createProgram } from "../../../requesters/Backend/ProgramRequester"

// Mirrors the defaults used by the per-unit Add Program page so the generated
// socio-demographic questions start active.
const SOCIO_DEMO_DEFAULTS = {
  ten_dwc: true,
  contact_info: true,
  age: true,
  job_level: true,
  sex: true,
  socialdemographic_lote: true,
  socialdemographic_aboriginal: true,
  socialdemographic_birth_country: true
}

function CreateProgramModal({ baseUrl, currentUser }) {
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)

  const [orgQuery, setOrgQuery] = useState("")
  const [debouncedOrgQuery, setDebouncedOrgQuery] = useState("")
  const [orgResults, setOrgResults] = useState([])
  const [searchingOrgs, setSearchingOrgs] = useState(false)

  const [selectedOrg, setSelectedOrg] = useState(null)
  const [units, setUnits] = useState([])
  const [loadingUnits, setLoadingUnits] = useState(false)
  const [unitId, setUnitId] = useState("")

  const [name, setName] = useState("")
  const [inflight, setInflight] = useState(false)
  const [error, setError] = useState(null)

  const resetState = () => {
    setOrgQuery("")
    setDebouncedOrgQuery("")
    setOrgResults([])
    setSelectedOrg(null)
    setUnits([])
    setUnitId("")
    setName("")
    setInflight(false)
    setError(null)
  }

  const handleOpen = () => {
    resetState()
    setShowModal(true)
  }

  const handleClose = () => setShowModal(false)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedOrgQuery(orgQuery), 300)
    return () => clearTimeout(t)
  }, [orgQuery])

  useEffect(() => {
    if (!showModal || selectedOrg || !debouncedOrgQuery) {
      setOrgResults([])
      return undefined
    }
    let active = true
    setSearchingOrgs(true)
    getOrganisations(baseUrl, { search: debouncedOrgQuery, per_page: 10 }, currentUser)
      .then(res => {
        if (active) setOrgResults(res?.response || [])
        return null
      })
      .catch(() => { if (active) setOrgResults([]) })
      .finally(() => { if (active) setSearchingOrgs(false) })
    return () => { active = false }
  }, [showModal, debouncedOrgQuery, selectedOrg, baseUrl, currentUser])

  const handleSelectOrg = org => {
    setSelectedOrg(org)
    setOrgResults([])
    setUnitId("")
    setUnits([])
    setLoadingUnits(true)
    getOrganisationDetail(baseUrl, org.id, currentUser)
      .then(res => {
        setUnits(res?.response?.json?.organisation?.units || [])
        return null
      })
      .catch(() => setUnits([]))
      .finally(() => setLoadingUnits(false))
  }

  const handleChangeOrg = () => {
    setSelectedOrg(null)
    setUnits([])
    setUnitId("")
    setOrgQuery("")
    setDebouncedOrgQuery("")
  }

  const canSubmit = Boolean(unitId) && Boolean(name.trim()) && !inflight

  const handleSubmit = () => {
    if (!canSubmit) return
    setError(null)
    setInflight(true)
    createProgram(baseUrl, { unit_id: unitId, name: name.trim(), ...SOCIO_DEMO_DEFAULTS }, currentUser)
      .then(res => {
        const programId = res?.response?.json?.program?.id
        if (!programId) throw new Error("Program id missing from response")
        navigate(`/backend/programs/${programId}`)
        return null
      })
      .catch(err => {
        const detail = err?.response?.data?.errors?.[0]?.detail
        setError(detail || "Program could not be created. Please try again.")
        setInflight(false)
      })
  }

  return (
    <>
      <button type="button" className="btn btn-outline-main btn-sm" onClick={handleOpen}>+ New Program</button>
      <Modal
        backdrop="static"
        show={showModal}
        onHide={handleClose}
        dialogClassName="create-program-modal"
        centered>
        <Modal.Header closeButton>
          <Modal.Title>New Program</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className="alert alert-danger" role="alert">{error}</div>}

          <div className="form-group">
            <label>Organisation</label>
            {selectedOrg ? (
              <div className="d-flex justify-content-between align-items-center">
                <span>{selectedOrg.name}</span>
                <button type="button" className="btn btn-outline-main btn-sm" onClick={handleChangeOrg}>Change</button>
              </div>
            ) : (
              <>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search organisation"
                  value={orgQuery}
                  onChange={e => setOrgQuery(e.target.value)} />
                {searchingOrgs && <small className="text-muted">Searching…</small>}
                {!searchingOrgs && debouncedOrgQuery && orgResults.length === 0 && (
                  <small className="text-muted">No organisations found</small>
                )}
                {orgResults.length > 0 && (
                  <ul className="org-results mt-1">
                    {orgResults.map(org => (
                      <li key={org.id}>
                        <button type="button" className="org-result" onClick={() => handleSelectOrg(org)}>
                          {org.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {selectedOrg && (
            <div className="form-group">
              <label>Unit</label>
              {loadingUnits && <p className="text-muted mb-0">Loading units…</p>}
              {!loadingUnits && units.length > 0 && (
                <select className="form-control" value={unitId} onChange={e => setUnitId(e.target.value)}>
                  <option value="">Select a unit</option>
                  {units.map(unit => (
                    <option key={unit.id} value={unit.id}>{unit.name}</option>
                  ))}
                </select>
              )}
              {!loadingUnits && units.length === 0 && (
                <p className="text-muted mb-0">This organisation has no units.</p>
              )}
            </div>
          )}

          <div className="form-group mb-0">
            <label>Program name</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. 30 Days Boost"
              value={name}
              onChange={e => setName(e.target.value)} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={inflight}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {inflight ? "Creating…" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default CreateProgramModal
